'use client';

import React, { useState, useCallback } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import {
  Calculator,
  TrendingUp,
  Home,
  DollarSign,
  BarChart2,
  Info,
  ChevronDown,
  ChevronUp,
  Star,
} from 'lucide-react';
import {
  RadialBarChart,
  RadialBar,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';

interface CalcInputs {
  propertyPrice: number;
  downPaymentPct: number;
  loanTermYears: number;
  annualInterestRate: number;
  monthlyRent: number;
  annualExpensesPct: number;
  annualAppreciationPct: number;
  purchaseCostsPct: number;
  holdingYears: number;
}

interface CalcResults {
  downPayment: number;
  loanAmount: number;
  monthlyMortgage: number;
  totalPurchaseCosts: number;
  totalInitialInvestment: number;
  annualGrossRent: number;
  annualExpenses: number;
  annualNetRent: number;
  grossRentalYield: number;
  netRentalYield: number;
  annualMortgageCost: number;
  annualCashFlow: number;
  monthlyCashFlow: number;
  projectedValue: number;
  totalRentReceived: number;
  totalMortgagePaid: number;
  totalExpensesPaid: number;
  capitalGain: number;
  totalReturn: number;
  totalROI: number;
  annualizedROI: number;
  investmentScore: number;
}

function formatTL(value: number): string {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(value);
}

function formatPct(value: number): string {
  return `%${value.toFixed(2)}`;
}

function calculateResults(inputs: CalcInputs): CalcResults {
  const {
    propertyPrice,
    downPaymentPct,
    loanTermYears,
    annualInterestRate,
    monthlyRent,
    annualExpensesPct,
    annualAppreciationPct,
    purchaseCostsPct,
    holdingYears,
  } = inputs;

  const downPayment = propertyPrice * (downPaymentPct / 100);
  const loanAmount = propertyPrice - downPayment;
  const purchaseCosts = propertyPrice * (purchaseCostsPct / 100);
  const totalInitialInvestment = downPayment + purchaseCosts;

  // Monthly mortgage (PMT formula)
  const monthlyRate = annualInterestRate / 100 / 12;
  const n = loanTermYears * 12;
  const monthlyMortgage =
    loanAmount > 0 && monthlyRate > 0
      ? (loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, n))) / (Math.pow(1 + monthlyRate, n) - 1)
      : loanAmount / n;

  const annualGrossRent = monthlyRent * 12;
  const annualExpenses = annualGrossRent * (annualExpensesPct / 100);
  const annualNetRent = annualGrossRent - annualExpenses;
  const grossRentalYield = (annualGrossRent / propertyPrice) * 100;
  const netRentalYield = (annualNetRent / propertyPrice) * 100;
  const annualMortgageCost = monthlyMortgage * 12;
  const annualCashFlow = annualNetRent - annualMortgageCost;
  const monthlyCashFlow = annualCashFlow / 12;

  // Projection
  const projectedValue = propertyPrice * Math.pow(1 + annualAppreciationPct / 100, holdingYears);
  const totalRentReceived = annualNetRent * holdingYears;
  const totalMortgagePaid = annualMortgageCost * holdingYears;
  const totalExpensesPaid = annualExpenses * holdingYears;
  const capitalGain = projectedValue - propertyPrice;
  const totalReturn = totalRentReceived - totalMortgagePaid + capitalGain;
  const totalROI = (totalReturn / totalInitialInvestment) * 100;
  const annualizedROI = (Math.pow(1 + totalROI / 100, 1 / holdingYears) - 1) * 100;

  // Investment Score (0-100)
  let score = 0;
  // Yield score (0-30)
  score += Math.min(30, (netRentalYield / 8) * 30);
  // Cash flow score (0-25)
  if (monthlyCashFlow > 0) score += Math.min(25, (monthlyCashFlow / 5000) * 25);
  // ROI score (0-25)
  score += Math.min(25, (annualizedROI / 15) * 25);
  // Appreciation score (0-20)
  score += Math.min(20, (annualAppreciationPct / 10) * 20);

  return {
    downPayment,
    loanAmount,
    monthlyMortgage,
    totalPurchaseCosts: purchaseCosts,
    totalInitialInvestment,
    annualGrossRent,
    annualExpenses,
    annualNetRent,
    grossRentalYield,
    netRentalYield,
    annualMortgageCost,
    annualCashFlow,
    monthlyCashFlow,
    projectedValue,
    totalRentReceived,
    totalMortgagePaid,
    totalExpensesPaid,
    capitalGain,
    totalReturn,
    totalROI,
    annualizedROI,
    investmentScore: Math.min(100, Math.round(score)),
  };
}

function ScoreGauge({ score }: { score: number }) {
  const color = score >= 70 ? '#22c55e' : score >= 45 ? '#f59e0b' : '#ef4444';
  const label = score >= 70 ? 'Mükemmel' : score >= 45 ? 'Orta' : 'Zayıf';
  const data = [{ name: 'Skor', value: score, fill: color }];

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-40 h-40">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            cx="50%"
            cy="50%"
            innerRadius="65%"
            outerRadius="90%"
            startAngle={180}
            endAngle={-180}
            data={data}
          >
            <RadialBar dataKey="value" cornerRadius={8} background={{ fill: '#f1f5f9' }} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-extrabold" style={{ color }}>{score}</span>
          <span className="text-xs font-bold text-muted-foreground">/ 100</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        <Star size={14} style={{ color }} fill={color} />
        <span className="text-sm font-bold" style={{ color }}>{label}</span>
      </div>
    </div>
  );
}

const DEFAULT_INPUTS: CalcInputs = {
  propertyPrice: 5000000,
  downPaymentPct: 30,
  loanTermYears: 20,
  annualInterestRate: 45,
  monthlyRent: 25000,
  annualExpensesPct: 20,
  annualAppreciationPct: 15,
  purchaseCostsPct: 4,
  holdingYears: 10,
};

export default function InvestmentCalculatorPage() {
  const [inputs, setInputs] = useState<CalcInputs>(DEFAULT_INPUTS);
  const [openSection, setOpenSection] = useState<string | null>('mortgage');

  const results = calculateResults(inputs);

  const handleChange = useCallback((key: keyof CalcInputs, value: string) => {
    const num = parseFloat(value.replace(/[^0-9.]/g, '')) || 0;
    setInputs((prev) => ({ ...prev, [key]: num }));
  }, []);

  const roiData = Array.from({ length: inputs.holdingYears }, (_, i) => {
    const year = i + 1;
    const projVal = inputs.propertyPrice * Math.pow(1 + inputs.annualAppreciationPct / 100, year);
    const rentIncome = results.annualNetRent * year;
    const mortgagePaid = results.annualMortgageCost * year;
    return {
      yıl: `Y${year}`,
      'Mülk Değeri': Math.round(projVal / 1000),
      'Kira Geliri': Math.round(rentIncome / 1000),
      'Kredi Ödemesi': Math.round(mortgagePaid / 1000),
    };
  });

  const InputField = ({
    label,
    fieldKey,
    suffix,
    min,
    max,
    step,
    tooltip,
  }: {
    label: string;
    fieldKey: keyof CalcInputs;
    suffix?: string;
    min?: number;
    max?: number;
    step?: number;
    tooltip?: string;
  }) => (
    <div>
      <div className="flex items-center gap-1 mb-1.5">
        <label className="text-xs font-semibold text-muted-foreground">{label}</label>
        {tooltip && (
          <div className="group relative">
            <Info size={12} className="text-muted-foreground cursor-help" />
            <div className="absolute bottom-5 left-0 w-48 bg-foreground text-white text-xs rounded-lg p-2 hidden group-hover:block z-10">
              {tooltip}
            </div>
          </div>
        )}
      </div>
      <div className="relative">
        <input
          type="number"
          value={inputs[fieldKey]}
          onChange={(e) => handleChange(fieldKey, e.target.value)}
          min={min}
          max={max}
          step={step ?? 1}
          className="w-full px-3 py-2.5 pr-10 border border-border rounded-xl text-sm font-semibold text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );

  const Section = ({
    id,
    title,
    icon,
    children,
  }: {
    id: string;
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode;
  }) => (
    <div className="card-elevated rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpenSection(openSection === id ? null : id)}
        className="w-full flex items-center justify-between px-6 py-4 text-left"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-bold text-foreground">{title}</span>
        </div>
        {openSection === id ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
      </button>
      {openSection === id && <div className="px-6 pb-6">{children}</div>}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16 lg:pt-18">
        {/* Hero */}
        <div className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-10 px-4">
          <div className="max-w-screen-xl mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Calculator size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-extrabold text-foreground">Yatırım Hesaplayıcı</h1>
                <p className="text-sm text-muted-foreground">Gayrimenkul yatırımınızı detaylı analiz edin</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-screen-xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Left: Inputs */}
            <div className="lg:col-span-2 space-y-4">
              <Section
                id="property"
                title="Mülk Bilgileri"
                icon={<Home size={16} className="text-primary" />}
              >
                <div className="grid grid-cols-1 gap-4 mt-2">
                  <InputField label="Mülk Fiyatı" fieldKey="propertyPrice" suffix="TL" tooltip="Satın alma fiyatı" />
                  <InputField label="Peşinat Oranı" fieldKey="downPaymentPct" suffix="%" min={0} max={100} step={5} tooltip="Toplam fiyatın yüzdesi" />
                  <InputField label="Alım Maliyetleri" fieldKey="purchaseCostsPct" suffix="%" min={0} max={20} step={0.5} tooltip="Tapu, vergi, komisyon vb. (genellikle %3-5)" />
                  <InputField label="Yıllık Değer Artışı" fieldKey="annualAppreciationPct" suffix="%" min={0} max={50} step={1} tooltip="Beklenen yıllık değer artış oranı" />
                  <InputField label="Yatırım Süresi" fieldKey="holdingYears" suffix="yıl" min={1} max={30} step={1} />
                </div>
              </Section>

              <Section
                id="mortgage"
                title="Kredi Simülasyonu"
                icon={<DollarSign size={16} className="text-accent" />}
              >
                <div className="grid grid-cols-1 gap-4 mt-2">
                  <InputField label="Kredi Vadesi" fieldKey="loanTermYears" suffix="yıl" min={1} max={30} step={1} />
                  <InputField label="Yıllık Faiz Oranı" fieldKey="annualInterestRate" suffix="%" min={0} max={100} step={0.5} tooltip="Yıllık nominal faiz oranı" />
                </div>
                <div className="mt-4 p-4 bg-muted rounded-xl space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Kredi Tutarı</span>
                    <span className="font-bold text-foreground">{formatTL(results.loanAmount)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Aylık Taksit</span>
                    <span className="font-bold text-primary">{formatTL(results.monthlyMortgage)}</span>
                  </div>
                </div>
              </Section>

              <Section
                id="rental"
                title="Kira Geliri"
                icon={<TrendingUp size={16} className="text-success" />}
              >
                <div className="grid grid-cols-1 gap-4 mt-2">
                  <InputField label="Aylık Kira" fieldKey="monthlyRent" suffix="TL" tooltip="Beklenen aylık kira geliri" />
                  <InputField label="Yıllık Gider Oranı" fieldKey="annualExpensesPct" suffix="%" min={0} max={50} step={1} tooltip="Bakım, sigorta, boşluk vb. (genellikle %15-25)" />
                </div>
              </Section>
            </div>

            {/* Right: Results */}
            <div className="lg:col-span-3 space-y-6">
              {/* Investment Score */}
              <div className="card-elevated rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                  <BarChart2 size={18} className="text-primary" />
                  <h2 className="font-bold text-foreground">Yatırım Skoru</h2>
                </div>
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <ScoreGauge score={results.investmentScore} />
                  <div className="flex-1 grid grid-cols-2 gap-4">
                    {[
                      { label: 'Brüt Kira Getirisi', value: formatPct(results.grossRentalYield), color: 'text-primary' },
                      { label: 'Net Kira Getirisi', value: formatPct(results.netRentalYield), color: 'text-success' },
                      { label: 'Aylık Nakit Akışı', value: formatTL(results.monthlyCashFlow), color: results.monthlyCashFlow >= 0 ? 'text-success' : 'text-danger' },
                      { label: 'Yıllık ROI', value: formatPct(results.annualizedROI), color: 'text-accent' },
                    ].map((item, i) => (
                      <div key={i} className="bg-muted rounded-xl p-3">
                        <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
                        <p className={`text-lg font-extrabold ${item.color}`}>{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Purchase Cost Breakdown */}
              <div className="card-elevated rounded-2xl p-6">
                <h2 className="font-bold text-foreground mb-4 flex items-center gap-2">
                  <Home size={16} className="text-primary" />
                  Satın Alma Maliyeti Dağılımı
                </h2>
                <div className="space-y-3">
                  {[
                    { label: 'Mülk Fiyatı', value: inputs.propertyPrice, pct: 100 },
                    { label: 'Peşinat', value: results.downPayment, pct: inputs.downPaymentPct },
                    { label: 'Kredi Tutarı', value: results.loanAmount, pct: 100 - inputs.downPaymentPct },
                    { label: 'Alım Maliyetleri', value: results.totalPurchaseCosts, pct: inputs.purchaseCostsPct },
                    { label: 'Toplam Başlangıç Yatırımı', value: results.totalInitialInvestment, pct: null, highlight: true },
                  ].map((item, i) => (
                    <div key={i} className={`flex items-center justify-between py-2 ${item.highlight ? 'border-t border-border pt-3 mt-1' : ''}`}>
                      <span className={`text-sm ${item.highlight ? 'font-bold text-foreground' : 'text-muted-foreground'}`}>{item.label}</span>
                      <div className="text-right">
                        <span className={`text-sm font-bold ${item.highlight ? 'text-primary' : 'text-foreground'}`}>{formatTL(item.value)}</span>
                        {item.pct !== null && (
                          <span className="text-xs text-muted-foreground ml-2">%{item.pct}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ROI Projection Chart */}
              <div className="card-elevated rounded-2xl p-6">
                <h2 className="font-bold text-foreground mb-4 flex items-center gap-2">
                  <TrendingUp size={16} className="text-success" />
                  {inputs.holdingYears} Yıllık Projeksiyon (Bin TL)
                </h2>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={roiData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="yıl" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v: number) => `${v}K TL`} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="Mülk Değeri" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Kira Geliri" fill="#22c55e" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Kredi Ödemesi" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Summary */}
              <div className="card-elevated rounded-2xl p-6">
                <h2 className="font-bold text-foreground mb-4 flex items-center gap-2">
                  <BarChart2 size={16} className="text-accent" />
                  {inputs.holdingYears} Yıl Sonunda Özet
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Tahmini Mülk Değeri', value: formatTL(results.projectedValue), color: 'text-primary' },
                    { label: 'Sermaye Kazancı', value: formatTL(results.capitalGain), color: 'text-success' },
                    { label: 'Toplam Kira Geliri', value: formatTL(results.totalRentReceived), color: 'text-success' },
                    { label: 'Toplam Kredi Ödemesi', value: formatTL(results.totalMortgagePaid), color: 'text-danger' },
                    { label: 'Toplam Getiri', value: formatTL(results.totalReturn), color: results.totalReturn >= 0 ? 'text-success' : 'text-danger' },
                    { label: 'Toplam ROI', value: formatPct(results.totalROI), color: results.totalROI >= 0 ? 'text-success' : 'text-danger' },
                  ].map((item, i) => (
                    <div key={i} className="bg-muted rounded-xl p-3">
                      <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
                      <p className={`text-base font-extrabold ${item.color}`}>{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
